/**
 * How big Turtley's question is drawn, given how much there is of it.
 *
 * The same idea as Rocky's clues and for the same reason: the television is a
 * fixed frame, and a card that is comfortable for "How much for two pencils?"
 * runs off the bottom for "You buy two notebooks and a banana. Your friend
 * buys three bananas. Who spent more?" over a shop of four things.
 *
 * Measured before this existed: a two-line question with four items pushed the
 * scroll region 20px past the screen, and a two-line question with three items
 * did the same. Everything shorter fitted with room to spare — so shrinking
 * every question to suit the longest would have made the ordinary case worse
 * to read for the sake of the rare one, which is the trade Rocky's clue tiers
 * were written to avoid.
 *
 * **Tiered on how much there is, not on how many lines it happens to take.**
 * Line count is a fact about the rendered box, so reading it means measuring
 * after laying out and then laying out again — and the answer changes with the
 * font, which is the thing being chosen. Characters and items are known before
 * anything is drawn.
 *
 * Three items is the ordinary shop and costs nothing here; only a fourth line
 * takes height the question then has to give back.
 */
const ORDINARY_SHOP = 3
const EXTRA_ITEM_WEIGHT = 20

/**
 * Past this, the question steps down a size.
 *
 * Measured rather than chosen, over ninety rounds across thirty seeds and five
 * levels. Questions of 38 and 58 characters over three items fit with room to
 * spare; one of about 70 over three, and ones of 91 to 100 over four, pushed
 * the frame 20px past the screen. Sixty-four sits between them.
 *
 * The first attempt weighed every item, threshold 78 — which made three items
 * alone exceed it, so *every* question shrank. Ninety rounds out of ninety at
 * the smaller size, which is the exact trade this tiering exists to avoid.
 */
const ROOMY_ENOUGH = 64

export interface TrolleyCard {
  /** The question as she reads it. */
  readonly prompt: string
  /** How many things are priced on the shop list above it. */
  readonly items: number
}

export function weightOf({ prompt, items }: TrolleyCard): number {
  return prompt.length + Math.max(0, items - ORDINARY_SHOP) * EXTRA_ITEM_WEIGHT
}

/**
 * The class the question is drawn with.
 *
 * Only the television changes. On a phone the card scrolls with the page and
 * always has, so there is nothing to reclaim and no reason to make a long
 * question harder to read.
 */
export function questionClassName(card: TrolleyCard): string {
  const base = 'text-(length:--text-heading) text-center leading-snug text-balance'

  if (weightOf(card) <= ROOMY_ENOUGH) return base

  return `${base} [[data-scale=tv]_&]:text-(length:--text-body)`
}
